import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logger } from 'src/analytics/KatalLogger';
import { getXptMetadata } from 'src/api/app-sync-services';
import { eLocalStorageKeys } from 'src/constants/generic-constants';
import { Status } from 'src/models/AuthContextModels';
import { ParsedMetadata, XptMetadata } from 'src/models/XptGenericModels';
import type { ThemeMode } from 'src/features/ag-grid/theme';
import { parseMetadata } from 'src/utils/xpt-metadata-utils';

// State interface definition for the Application level metadata.
interface AppMetadataState {
  themeMode: ThemeMode;

  xptMetadataStatus: Status;
  xptMetadata: XptMetadata[];
  parsedMetadata: ParsedMetadata;
}

// Function to get the initial theme from URL params (when embedded) or localStorage
const getInitialTheme = (): ThemeMode => {
  // When embedded, trust the parent's theme param over localStorage
  const urlParams = new URLSearchParams(window.location.search);
  const isDarkModeParam = urlParams.get('isDarkMode');
  const isEmbedded = urlParams.get('embed') === 'true';
  if (isEmbedded && isDarkModeParam !== null) {
    return isDarkModeParam === 'true' ? 'dark' : 'light';
  }
  // Fallback to localStorage for standalone mode
  const savedTheme = localStorage.getItem(eLocalStorageKeys.Theme);
  // Support legacy theme value from pre-v35 AG Grid upgrade
  return savedTheme === 'dark' || savedTheme === 'ag-theme-quartz-dark' ? 'dark' : 'light';
};

export const appMetadataInitialState: AppMetadataState = {
  themeMode: getInitialTheme(),
  xptMetadataStatus: Status.NotInitiated,
  xptMetadata: [],
  parsedMetadata: {}
};

export const fetchXptMetadata = createAsyncThunk('xPTMapper/fetchXptMetadata', async () => {
  const response: XptMetadata[] = await getXptMetadata();
  return response;
});

const appMetadataSlice = createSlice({
  name: 'appMetadata',
  initialState: appMetadataInitialState,
  reducers: {
    toggleTheme: (state, action: PayloadAction<boolean>) => {
      const themeMode: ThemeMode = action.payload ? 'dark' : 'light';
      state.themeMode = themeMode;
      localStorage.setItem(eLocalStorageKeys.Theme, themeMode);
    }
  },
  extraReducers: (builder) => {
    // Handle fetchExpenseTypes lifecycle actions
    builder.addCase(fetchXptMetadata.pending, (state) => {
      state.xptMetadataStatus = Status.Loading;
    });
    builder.addCase(fetchXptMetadata.fulfilled, (state, action: PayloadAction<XptMetadata[]>) => {
      state.xptMetadata = action.payload;
      try {
        state.parsedMetadata = parseMetadata(action.payload);
      } catch (error: any) {
        logger.error('Error parsing xpt metadata:', error);
        state.parsedMetadata = {};
      }
      state.xptMetadataStatus = Status.Completed;
    });
    builder.addCase(fetchXptMetadata.rejected, (state) => {
      state.xptMetadataStatus = Status.Failed;
    });
  }
});

export const { toggleTheme } = appMetadataSlice.actions;
export default appMetadataSlice.reducer;
